import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { AlertTriangle } from "lucide-react";

interface ConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  confirmText: string;
  onConfirm: () => void;
  onCancel?: () => void;
  requireInput?: string; // Text that must be typed to confirm
  variant?: "destructive" | "default";
  isPending?: boolean;
}

export function ConfirmModal({
  open,
  onOpenChange,
  title,
  subtitle,
  confirmText,
  onConfirm,
  onCancel,
  requireInput,
  variant = "destructive",
  isPending = false,
}: ConfirmModalProps) {
  const [inputValue, setInputValue] = useState("");
  const isInputRequired = Boolean(requireInput);
  const isConfirmDisabled = isInputRequired ? inputValue !== requireInput : false;

  // Reset input when modal opens/closes
  useEffect(() => {
    if (!open) {
      setInputValue("");
    }
  }, [open]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const handleCancel = () => {
    onOpenChange(false);
    onCancel?.();
  };

  const handleConfirm = () => {
    if (isConfirmDisabled || isPending) return;
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {variant === "destructive" && (
              <AlertTriangle className="w-5 h-5 text-destructive" />
            )}
            {title}
          </DialogTitle>
          {subtitle && (
            <DialogDescription className="text-left">
              {subtitle}
            </DialogDescription>
          )}
        </DialogHeader>

        {isInputRequired && (
          <div className="space-y-2">
            <Label htmlFor="confirm-input">
              Type <strong className="font-mono bg-muted px-1 rounded">{requireInput}</strong> to confirm
            </Label>
            <Input
              id="confirm-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Type "${requireInput}" to confirm`}
              autoFocus
              data-testid="confirm-input"
            />
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
            data-testid="button-cancel"
          >
            Cancel
          </Button>
          <Button
            variant={variant}
            onClick={handleConfirm}
            disabled={isConfirmDisabled || isPending}
            data-testid="button-confirm"
          >
            {isPending ? "Please wait..." : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}